import { Formik } from 'formik'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  CustomerUploadBusinessDetails,
  CustomerUploadUpdatedBusinessDetails,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { CustomerBusinessDetailsFormValues } from './CustomerBusinessDetailsForm.interface'
import { CustomerOnboardingBusinessDetailsSchema } from './CustomerBusinessDetailsForm.validator'
import tw from 'twin.macro'
import { showToast } from 'src/utils/toast'
import { Button } from 'src/components/shared/Button'
import { handleApiError } from 'src/utils/errorHandlers'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { getNotCompletedCustomerOnboardingCollectDataIdByType } from 'src/selectors/customer'
import { customerComplianceStepTitles } from 'src/constants/customer/constants'
import { CustomerCollectedDataType } from 'src/constants/customer/enums'
import { customerComplianceStepIcons } from 'src/constants/customer/icons'
import { CustomerFormHeader } from '../shared/CustomerFormHeader'

const defaultInitialValues: CustomerBusinessDetailsFormValues = {
  doesBusinessAdvertise: '',
  hasQualityProgram: '',
  qualityProgramDetails: '',
  vehicleAccessDetails: '',
  coveredCitiesDetails: '',
}

const radioOptions = [
  {
    label: 'Yes',
    value: 'yes',
  },
  {
    label: 'No',
    value: 'no',
  },
]

interface Props {
  stepNumber?: number
  stepCount?: number
  initialValues?: CustomerBusinessDetailsFormValues
  onSubmit?: () => void
}

export const CustomerBusinessDetailsForm = ({
  stepNumber,
  stepCount,
  initialValues,
  onSubmit,
}: Props) => {
  const dispatch = useDispatch()

  const notCompletedCustomerOnboardingCollectDataId = useSelector(
    getNotCompletedCustomerOnboardingCollectDataIdByType(CustomerCollectedDataType.BusinessDetails),
  )

  const isResubmit = !!initialValues

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFormSubmit = async ({
    doesBusinessAdvertise,
    hasQualityProgram,
    ...otherValues
  }: CustomerBusinessDetailsFormValues) => {
    setIsLoading(true)

    const res = {
      ...otherValues,
      doesBusinessAdvertise: doesBusinessAdvertise === 'yes',
      hasQualityProgram: hasQualityProgram === 'yes',
      collectedDataId: notCompletedCustomerOnboardingCollectDataId || undefined,
    }

    if (isResubmit) {
      delete res.collectedDataId
    }

    try {
      if (isResubmit) {
        await createAsyncAction(dispatch, CustomerUploadUpdatedBusinessDetails.request(res))
      } else {
        await createAsyncAction(dispatch, CustomerUploadBusinessDetails.request(res))
      }

      showToast('Business details submitted successfully', {
        variant: 'success',
      })

      if (onSubmit) {
        onSubmit()
      }
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues || defaultInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={CustomerOnboardingBusinessDetailsSchema}
    >
      {({ handleSubmit }) => (
        <div css={tw`w-[318px] bg-white rounded-lg`}>
          <CustomerFormHeader
            title={stepCount ? undefined : 'Resubmit info'}
            stepCount={stepCount}
            stepNumber={stepNumber}
          />
          <div css={tw`px-4 pt-6 py-2`}>
            {customerComplianceStepIcons[CustomerCollectedDataType.BusinessDetails]}
            <Typography variant="h4" css={tw`mt-2 mb-2`}>
              {customerComplianceStepTitles[CustomerCollectedDataType.BusinessDetails]}
            </Typography>
            <div css={tw`w-full h-[1px] bg-[#EDEDED]`} />
            <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-4 mt-2`}>
              Tell us about your business.
            </Typography>
            <div css={tw`w-full mb-4`}>
              <FormikRadioGroup
                name="doesBusinessAdvertise"
                label="Does your business advertise?*"
                options={radioOptions}
              />
            </div>
            <div css={tw`w-full mb-4`}>
              <FormikRadioGroup
                name="hasQualityProgram"
                label="Do you have a quality program?*"
                options={radioOptions}
              />
            </div>
            <FormikTextArea
              name="qualityProgramDetails"
              css={tw`w-full mb-4`}
              label="Tell us more about your quality program or how you comply with motor carrier authority & safety regulations"
              placeholder="Tell us more!"
            />
            <FormikTextArea
              name="vehicleAccessDetails"
              css={tw`w-full mb-4`}
              label="What sort of vehicles do you have access to?"
              placeholder="Tell us more!"
            />
            <FormikTextArea
              name="coveredCitiesDetails"
              css={tw`w-full mb-4`}
              label="What cities can you cover?"
              placeholder="Tell us more!"
            />
          </div>
          <div css={tw`px-4 py-3`}>
            <Button
              variant="contained"
              size="small"
              loading={isLoading}
              className={tw`w-full`}
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </Formik>
  )
}
